import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import type { EventDetailDTO, EventOptionDTO } from "../../shared/dto";
import { uploadsUrl } from "../lib/uploader";
import { formatEventDate, fmtDateTime } from "../lib/format";
import { RichTextRender } from "../components/RichTextRender";
import {
  StatusBadge,
  Loading,
  ErrorBanner,
  SuccessBanner,
} from "../components/ui";

const APPLICATION_STATUS_LABELS: Record<string, string> = {
  pending_payment: "決済待ち",
  applied: "申込中",
  cancelled: "キャンセル",
  checked_in: "受付済み",
  no_show: "不参加",
};
const APPLICATION_STATUS_TONE: Record<
  string,
  "success" | "warning" | "danger" | "neutral"
> = {
  pending_payment: "warning",
  applied: "neutral",
  cancelled: "neutral",
  checked_in: "success",
  no_show: "danger",
};

const MODE_LABEL: Record<string, string> = {
  offline: "来場",
  online: "オンライン",
  hybrid: "ハイブリッド",
};

interface EventDetailResp extends EventDetailDTO {
  myApplication?: {
    id: string;
    applicationStatus: string;
    participationMode: string;
    appliedAt: string;
    withSocial?: boolean;
  } | null;
  remainingCapacity: number | null;
  hasSocial?: boolean;
  socialCapacity?: number | null;
  socialFee?: number;
  socialVenue?: string | null;
  socialStartAt?: string | null;
  socialAppliedCount?: number;
  socialRemainingCapacity?: number | null;
  cancelDeadlineAt?: string | null;
  canCancel?: boolean;
}

export function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [data, setData] = useState<EventDetailResp | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<"offline" | "online" | "hybrid">("offline");
  const [withSocial, setWithSocial] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) return;
    api.get<EventDetailResp>(`/events/${eventId}`).then((d) => {
      setData(d);
      // ハイブリッドは会員が来場/オンラインを選ぶ。既定は来場。
      // online のみのイベントは online 固定。
      setMode(d.participationMode === "online" ? "online" : "offline");
      const required = (d.options ?? [])
        .filter((o) => o.isRequired)
        .map((o) => o.id);
      setSelected(new Set(required));
    });
  }, [eventId]);

  if (!data) return <Loading />;

  const apply = async () => {
    setBusy(true);
    setErr(null);
    setMsg(null);
    try {
      const r = await api.post<{ ok: boolean; needsPayment: boolean }>(
        `/events/${data.id}/apply`,
        { participationMode: mode, optionIds: Array.from(selected), withSocial },
      );
      setMsg(
        r.needsPayment ? "決済画面に進んでください。" : "申込が完了しました。",
      );
      const d = await api.get<EventDetailResp>(`/events/${data.id}`);
      setData(d);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const full = data.remainingCapacity === 0;
  const { date, time } = formatEventDate(data.eventStartAt);
  const thumb = uploadsUrl(data.thumbnailKey);

  return (
    <article className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/events"
        className="inline-block text-sm text-tora-ink/60 hover:text-tora"
      >
        ← イベント一覧へ
      </Link>

      <header className="space-y-3">
        <p className="text-[11px] text-tora-accent tracking-widest font-medium">
          EVENT
        </p>
        <h2 className="text-2xl md:text-3xl font-bold text-tora leading-tight">
          {data.title}
        </h2>
        {data.summary && (
          <p className="text-base md:text-lg text-tora-ink/70 leading-relaxed">
            {data.summary}
          </p>
        )}

        {/* meta row: 日時 (2 行) / モード / 会場 */}
        <div className="pt-2 space-y-1 text-sm">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-bold text-tora">{date}</span>
            {time && <span className="text-tora-ink/70">{time}</span>}
          </div>
          <p className="text-xs text-tora-ink/60">
            {MODE_LABEL[data.participationMode] ?? data.participationMode}
            {data.venueName ? ` / ${data.venueName}` : ""}
          </p>
        </div>

        {/* ステータスバッジ集約 */}
        <div className="flex flex-wrap gap-1.5">
          {data.myApplication && (
            <StatusBadge
              tone={
                APPLICATION_STATUS_TONE[data.myApplication.applicationStatus] ??
                "neutral"
              }
              bold={data.myApplication.applicationStatus === "checked_in"}
            >
              {APPLICATION_STATUS_LABELS[data.myApplication.applicationStatus] ??
                data.myApplication.applicationStatus}
            </StatusBadge>
          )}
          {full ? (
            <StatusBadge tone="danger" bold>
              満席
            </StatusBadge>
          ) : data.remainingCapacity != null ? (
            <StatusBadge tone="neutral">
              残 {data.remainingCapacity} 席
            </StatusBadge>
          ) : null}
        </div>
      </header>

      {thumb && (
        <img
          src={thumb}
          alt=""
          className="w-full h-auto max-h-[70vh] object-contain rounded-xl border border-black/10 bg-black/5 shadow-sm"
        />
      )}

      <RichTextRender html={data.description} />

      <section className="card p-5 md:p-6 border-l-4 border-l-tora-accent space-y-3">
        <h3 className="font-bold text-lg text-tora">申込</h3>

        {data.myApplication ? (
          <div className="space-y-3">
            <div className="text-sm text-tora-ink/80">
              申し込み済みです。
              {data.myApplication.withSocial && (
                <span className="ml-1 font-semibold text-tora">(懇親会参加)</span>
              )}
            </div>
            {data.myApplication.applicationStatus === "checked_in" && (
              <Link
                to={`/events/${data.id}/survey`}
                className="btn-primary text-sm inline-flex items-center gap-1"
              >
                📝 振り返りアンケートに回答する
              </Link>
            )}
            {data.canCancel && ["pending_payment", "applied"].includes(data.myApplication.applicationStatus) && (
              <button
                className="btn-outline text-sm"
                disabled={busy}
                onClick={async () => {
                  if (!confirm("この申込をキャンセルしますか？")) return;
                  setBusy(true);
                  setErr(null);
                  try {
                    await api.post(`/events/${data.id}/cancel`);
                    setMsg("キャンセルしました");
                    const d = await api.get<EventDetailResp>(`/events/${data.id}`);
                    setData(d);
                  } catch (e) {
                    setErr((e as Error).message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {busy ? "処理中…" : "申込をキャンセル"}
              </button>
            )}
            {!data.canCancel && (
              <p className="text-xs text-tora-ink/50">キャンセル締切を過ぎています</p>
            )}
            {err && <ErrorBanner>{err}</ErrorBanner>}
            {msg && <SuccessBanner>{msg}</SuccessBanner>}
          </div>
        ) : (
          <>
            <div>
              <label className="label">参加方法</label>
              {data.participationMode === "hybrid" ? (
                <select
                  className="input"
                  value={mode}
                  onChange={(e) => setMode(e.target.value as typeof mode)}
                >
                  <option value="offline">来場</option>
                  <option value="online">オンライン</option>
                </select>
              ) : (
                <p className="input bg-black/[0.03] text-tora-ink/70">
                  {data.participationMode === "online" ? "オンライン" : "来場"}
                </p>
              )}
            </div>

            {data.options && data.options.length > 0 && (
              <div>
                <label className="label">オプション</label>
                <ul className="space-y-2">
                  {data.options.map((o: EventOptionDTO) => (
                    <li
                      key={o.id}
                      className="text-sm flex items-center gap-2"
                    >
                      <input
                        id={`opt-${o.id}`}
                        type="checkbox"
                        disabled={o.isRequired}
                        checked={selected.has(o.id)}
                        onChange={(e) => {
                          const s = new Set(selected);
                          if (e.target.checked) s.add(o.id);
                          else s.delete(o.id);
                          setSelected(s);
                        }}
                      />
                      <label
                        htmlFor={`opt-${o.id}`}
                        className="flex-1 flex items-center justify-between gap-2"
                      >
                        <span>
                          {o.name}
                          {o.isRequired && (
                            <span className="ml-1 text-xs text-red-600">
                              必須
                            </span>
                          )}
                        </span>
                        {o.priceAmount > 0 && (
                          <span className="font-mono text-tora-ink/70">
                            ¥{o.priceAmount.toLocaleString()}
                          </span>
                        )}
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.hasSocial && (
              <div className="rounded-lg border border-orange-300/50 bg-orange-50/30 p-4 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer text-sm font-semibold">
                  <input
                    type="checkbox"
                    checked={withSocial}
                    onChange={(e) => setWithSocial(e.target.checked)}
                  />
                  懇親会にも参加する
                </label>
                <div className="text-xs text-tora-ink/60 space-y-0.5">
                  {data.socialFee != null && data.socialFee > 0 && (
                    <p>参加費: ¥{data.socialFee.toLocaleString()}</p>
                  )}
                  {data.socialVenue && <p>会場: {data.socialVenue}</p>}
                  {data.socialStartAt && (
                    <p>開始: {fmtDateTime(data.socialStartAt)}</p>
                  )}
                  {data.socialRemainingCapacity != null && (
                    <p>残席: {data.socialRemainingCapacity}</p>
                  )}
                </div>
              </div>
            )}

            {err && <ErrorBanner>{err}</ErrorBanner>}
            {msg && <SuccessBanner>{msg}</SuccessBanner>}
            <button
              className="btn-primary btn-lg w-full md:w-auto md:px-8"
              onClick={apply}
              disabled={busy || full}
            >
              {busy
                ? "送信中…"
                : full
                  ? "満席のため申込できません"
                  : "申し込む 🎫"}
            </button>
          </>
        )}
      </section>
    </article>
  );
}
